/**
 * Vault-focused candid mappers (ported from frontend/src/lib/candid-helpers.ts).
 * Governance proposal builders intentionally omitted for mobile client scope.
 */
import type { Principal } from "@icp-sdk/core/principal";
import type {
	CollateralParams as CandidCollateralParams,
	CollateralType as CandidCollateralType,
	EnrichedVault as CandidEnrichedVault,
	PortfolioSummary as CandidPortfolioSummary,
	PriceFeed as CandidPriceFeed,
	ProtocolParams as CandidProtocolParams,
	Token as CandidToken,
	Vault as CandidVault,
	VaultStatus as CandidVaultStatus,
} from "./declarations/protocol.did.d.ts";

export type CollateralKey = "ICP" | "ckBTC" | "ckETH";

export const CollateralType = {
	ICP: "ICP",
	ckBTC: "ckBTC",
	ckETH: "ckETH",
} as const satisfies Record<CollateralKey, CollateralKey>;

export const COLLATERAL_KEYS: CollateralKey[] = ["ICP", "ckBTC", "ckETH"];

export function toCandidCollateral(key: CollateralKey): CandidCollateralType {
	switch (key) {
		case "ICP":
			return { ICP: null };
		case "ckBTC":
			return { ckBTC: null };
		case "ckETH":
			return { ckETH: null };
	}
}

export function fromCandidCollateral(
	ct: CandidCollateralType | CollateralKey,
): CollateralKey {
	if (typeof ct === "string") return ct;
	if ("ICP" in ct) return "ICP";
	if ("ckBTC" in ct) return "ckBTC";
	return "ckETH";
}

export type TokenKey = "USDM" | "MMK";

export const Token = {
	USDM: "USDM",
	MMK: "MMK",
} as const satisfies Record<TokenKey, TokenKey>;

export function toCandidToken(key: TokenKey): CandidToken {
	return key === "USDM" ? { USDM: null } : { MMK: null };
}

export function fromCandidToken(t: CandidToken): TokenKey {
	return "USDM" in t ? "USDM" : "MMK";
}

export const E8S = 100_000_000n;

export function amountToNat(amount: number): bigint {
	return BigInt(Math.round(amount * Number(E8S)));
}

export function natToAmount(n: bigint): number {
	return Number(n) / Number(E8S);
}

export type Vault = {
	id: bigint;
	owner: Principal;
	collateral: Array<[CollateralKey, number]>;
	debt: number;
	created: bigint;
	lastUpdated: bigint;
};

export function mapVault(v: CandidVault): Vault {
	return {
		id: v.id,
		owner: v.owner,
		collateral: v.collateral.map(([ct, amt]) => [
			fromCandidCollateral(ct),
			natToAmount(amt),
		]),
		debt: natToAmount(v.debt),
		created: v.created,
		lastUpdated: v.lastUpdated,
	};
}

export type PriceFeed = {
	asset: CollateralKey;
	price: number;
	lastUpdated: bigint;
	pendingPrice: number | null;
	pendingActivatesAt: bigint | null;
	source: string | null;
	providerCount: number | null;
};

export function mapPriceFeed(f: CandidPriceFeed): PriceFeed {
	return {
		asset: fromCandidCollateral(f.asset),
		price: natToAmount(f.price),
		lastUpdated: f.lastUpdated,
		pendingPrice:
			f.pendingPrice.length > 0 ? natToAmount(f.pendingPrice[0]!) : null,
		pendingActivatesAt:
			f.pendingActivatesAt.length > 0 ? f.pendingActivatesAt[0]! : null,
		source: f.source.length > 0 ? f.source[0]! : null,
		providerCount:
			f.providerCount.length > 0 ? Number(f.providerCount[0]!) : null,
	};
}

export type CollateralParams = {
	collateralType: CollateralKey;
	minCollateralRatio: number;
	liquidationThreshold: number;
	stabilityFeeBps: bigint;
	debtCeiling: number;
};

export function mapCollateralParams(
	p: CandidCollateralParams,
): CollateralParams {
	return {
		collateralType: fromCandidCollateral(p.collateralType),
		minCollateralRatio: natToAmount(p.minCollateralRatioE8),
		liquidationThreshold: natToAmount(p.liquidationThresholdE8),
		stabilityFeeBps: p.stabilityFeeBps,
		debtCeiling: natToAmount(p.debtCeiling),
	};
}

export type ProtocolParamsUi = {
	liquidationThreshold: number;
	minCollateralRatio: number;
	liquidationBonusBps: bigint;
	savingsRateBps: bigint;
	executionDelayHours: bigint;
	globalDebtCeiling: number;
	totalDebt: number;
	mintPaused: boolean;
	liquidatePaused: boolean;
	globalPaused: boolean;
};

export function mapProtocolParams(p: CandidProtocolParams): ProtocolParamsUi {
	return {
		liquidationThreshold: natToAmount(p.liquidationThresholdE8),
		minCollateralRatio: natToAmount(p.minCollateralRatioE8),
		liquidationBonusBps: p.liquidationBonusBps,
		savingsRateBps: p.savingsRateBps,
		executionDelayHours: p.executionDelayHours,
		globalDebtCeiling: natToAmount(p.globalDebtCeiling),
		totalDebt: natToAmount(p.totalDebt),
		mintPaused: p.mintPaused,
		liquidatePaused: p.liquidatePaused,
		globalPaused: p.globalPaused,
	};
}

export type VaultStatusKey = "Active" | "AtRisk" | "Finished" | "Liquidated";

export function fromCandidVaultStatus(s: CandidVaultStatus): VaultStatusKey {
	if ("Active" in s) return "Active";
	if ("AtRisk" in s) return "AtRisk";
	if ("Finished" in s) return "Finished";
	return "Liquidated";
}

export type EnrichedVaultUi = {
	vault: Vault;
	status: VaultStatusKey;
	collateralValueUsd: number;
	ltv: number;
	healthFactor: number;
	recommendation: string | null;
};

export function mapEnrichedVault(ev: CandidEnrichedVault): EnrichedVaultUi {
	const vault = mapVault(ev.vault);
	return {
		vault,
		status: fromCandidVaultStatus(ev.status),
		collateralValueUsd: natToAmount(ev.collateralValueE8),
		ltv: Number(ev.ltvBps) / 100,
		healthFactor: natToAmount(ev.healthFactorE8),
		recommendation:
			ev.recommendation.length > 0 ? ev.recommendation[0]! : null,
	};
}

export type PortfolioSummaryUi = {
	totalCollateralUsd: number;
	totalDebt: number;
	avgHealth: number;
	atRiskCount: number;
	vaultCount: number;
};

export function mapPortfolioSummary(
	s: CandidPortfolioSummary,
): PortfolioSummaryUi {
	return {
		totalCollateralUsd: natToAmount(s.totalCollateralUsdE8),
		totalDebt: natToAmount(s.totalDebtE8),
		avgHealth: natToAmount(s.avgHealthFactorE8),
		atRiskCount: Number(s.atRiskCount),
		vaultCount: Number(s.vaultCount),
	};
}
